import type { FC } from "react";
import {
  Button,
  Checkbox,
  Group,
  Select,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useOrderRedoRequest } from "/@/pages/orders/useOrderRedoRequest";
import { type Order } from "@jasper/shared";
import type { OrderPageState } from "../useOrderPage";
import { OrderRequestPanel, REQUEST_ERROR_MESSAGE } from "./OrderRequestPanel";

type OrderRedoPanelProps = {
  order: Order;
  state: OrderPageState;
};

const PANEL_TITLE = "Demander une refabrication";

export const OrderRedoPanel: FC<OrderRedoPanelProps> = ({ order, state }) => {
  const { refetch, requests, bankHolidays, connectedUserEmail } = state;
  const {
    pickupForm,
    submit,
    confirmSent,
    setRedoRequestMessage,
    canRequestPickup,
    isPickupRequested,
    setIsPickupRequested,
    pickupDateOptions,
    clinicCloseTime,
    setClinicCloseTime,
    isLoading,
    error,
    newDeliveryDate,
  } = useOrderRedoRequest({
    order,
    bankHolidays,
    userEmail: connectedUserEmail,
  });

  const closeAndRefetch = () => {
    refetch();
    requests.redo.close();
  };

  if (newDeliveryDate) {
    return (
      <OrderRequestPanel
        title={PANEL_TITLE}
        subtitle="Votre demande est prise en compte."
        onClose={closeAndRefetch}
      >
        <Group justify="space-between" gap="s3">
          <Title
            order={4}
          >{`Date de livraison estimée : ${newDeliveryDate}`}</Title>
          <Button size="sm" onClick={() => confirmSent(closeAndRefetch)}>
            Valider
          </Button>
        </Group>
      </OrderRequestPanel>
    );
  }

  return (
    <OrderRequestPanel
      title={PANEL_TITLE}
      subtitle="Décrivez ce qui ne convient pas : le laboratoire relance la fabrication."
      errorMessage={error ? REQUEST_ERROR_MESSAGE : null}
      submitLabel="Envoyer la demande"
      isSubmitting={isLoading}
      onSubmit={submit}
      onClose={requests.redo.close}
    >
      <Textarea
        aria-label="Motif de la refabrication"
        placeholder="Décrivez ce qui ne convient pas"
        onChange={event => setRedoRequestMessage(event.target.value)}
        autosize
        minRows={3}
        maxRows={12}
      />
      {canRequestPickup && (
        <Checkbox
          checked={isPickupRequested}
          onChange={event => setIsPickupRequested(event.target.checked)}
          label="Souhaitez-vous également un ramassage ?"
        />
      )}
      {isPickupRequested && (
        <Group grow align="flex-start" gap="s4">
          <Select
            label="Date du ramassage"
            data={pickupDateOptions}
            value={pickupForm.watch("date")}
            onChange={value => value && pickupForm.setValue("date", value)}
            allowDeselect={false}
          />
          <TextInput
            type="time"
            label="Heure de fermeture du cabinet"
            value={clinicCloseTime}
            onChange={event => setClinicCloseTime(event.target.value)}
          />
        </Group>
      )}
    </OrderRequestPanel>
  );
};
